import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AnnouncementBar.css';

const AnnouncementBar = ({ announcements }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);
  const messageRef = useRef(null);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const stored = localStorage.getItem('dismissedAnnouncements');
    return stored ? JSON.parse(stored) : [];
  });

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    ann => !dismissedIds.includes(ann.id)
  );

  const currentAnnouncement = visibleAnnouncements[currentIndex];

  // Check if text overflows and needs marquee
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && messageRef.current) {
        const containerWidth = contentRef.current.offsetWidth;
        const messageWidth = messageRef.current.scrollWidth;
        setIsOverflowing(messageWidth > containerWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [currentAnnouncement]);

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  const handleDismiss = (id) => {
    const newDismissedIds = [...dismissedIds, id];
    setDismissedIds(newDismissedIds);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissedIds));

    // Move to next announcement if available
    if (currentIndex >= visibleAnnouncements.length - 1 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < visibleAnnouncements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(visibleAnnouncements.length - 1);
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'announcement-bar--urgent';
      case 'high':
        return 'announcement-bar--high';
      case 'normal':
        return 'announcement-bar--normal';
      case 'low':
        return 'announcement-bar--low';
      default:
        return 'announcement-bar--normal';
    }
  };

  return (
    <AnimatePresence mode="wait">
      {currentAnnouncement && (
        <motion.div
          key={currentAnnouncement.id}
          className={`announcement-bar ${getPriorityClass(currentAnnouncement.priority)}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="announcement-bar__container">
            <div className="announcement-bar__icon">
              <i className="fas fa-bullhorn"></i>
            </div>

            <div className="announcement-bar__content" ref={contentRef}>
              <div className={`announcement-bar__text-wrapper ${isOverflowing ? 'marquee' : ''}`}>
                <div className="announcement-bar__text-inner" ref={messageRef}>
                  <strong className="announcement-bar__title">
                    {currentAnnouncement.title}
                  </strong>
                  <span
                    className="announcement-bar__message"
                    dangerouslySetInnerHTML={{ __html: currentAnnouncement.message }}
                  />
                </div>
                {/* Duplicate for seamless marquee loop */}
                {isOverflowing && (
                  <div className="announcement-bar__text-inner" aria-hidden="true">
                    <strong className="announcement-bar__title">
                      {currentAnnouncement.title}
                    </strong>
                    <span
                      className="announcement-bar__message"
                      dangerouslySetInnerHTML={{ __html: currentAnnouncement.message }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="announcement-bar__controls">
              {visibleAnnouncements.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="announcement-bar__nav"
                    aria-label="Previous announcement"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="announcement-bar__counter">
                    {currentIndex + 1} / {visibleAnnouncements.length}
                  </span>
                  <button
                    onClick={handleNext}
                    className="announcement-bar__nav"
                    aria-label="Next announcement"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </>
              )}

              {currentAnnouncement.dismissible && (
                <button
                  onClick={() => handleDismiss(currentAnnouncement.id)}
                  className="announcement-bar__dismiss"
                  aria-label="Dismiss announcement"
                >
                  <i className="fas fa-times"></i>
                  <span>Dismiss</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementBar;
